import * as R from 'ramda'
import {
  select,
  fork,
  join,
  put,
  all,
  call,
  takeEvery,
} from 'redux-saga/effects'

import {
  updateTablesAction,
  clearTables,
} from 'containers/Entity/actions'

import {
  get,
  patch,
  post,
  del
} from 'helpers/RequestUtil'

import {
  fetchEmployeeImage,
} from 'containers/User/EmployeeImage/actions'

import {
  employeeUsernameSelector,
} from 'containers/User/EmployeeImage/selectors'

import {
  createSubstituteSelector,
  patchSubstituteSelector,
} from './selectors'

import {
  FETCH_SUBSTITUTES,
  POST_SUBSTITUTES,
  PATCH_SUBSTITUTES,
  SAVE_SUBSTITUTES,
  DELETE_SUBSTITUTES,
} from './constants'

import {
  removeSubstitutes,
  resetSubstitutes,
  updateSubstitutes,
  fetchSubstitutes as fetchSubstitutesAction,
  fetchSubstitutesStart,
  fetchSubstitutesSuccess,
  fetchSubstitutesError,
  postSubstitutes as postSubstitutesAction,
  postSubstitutesStart,
  postSubstitutesSuccess,
  postSubstitutesError,
  patchSubstitutes as patchSubstitutesAction,
  patchSubstitutesStart,
  patchSubstitutesSuccess,
  patchSubstitutesError,
  deleteSubstitutesStart,
  deleteSubstitutesSuccess,
  deleteSubstitutesError,
  startFetchSubstitutes,
  startPostSubstitutes,
  startPatchSubstitutes,
  startDeleteSubstitutes,
} from './actions'

const url = '/api/fulcrum/approval_substitutes'

function* fetchSubstitutes() {
  try {
    yield put(startFetchSubstitutes())
    yield put(fetchSubstitutesStart())
    const data = yield call(get, url)
    yield put(updateTablesAction(data))
    yield put(fetchSubstitutesSuccess())
    return data
  } catch(error) {
    yield put(fetchSubstitutesError(error))
  }
}

function* patchSubstitutes(id, params) {
  try {
    yield put(startPatchSubstitutes())
    yield put(patchSubstitutesStart())
    yield call(patch, id, 'approval_substitutes', params, url)
    yield put(patchSubstitutesSuccess())
  } catch(error) {
    yield put(patchSubstitutesError(error))
  }
}

function* postSubstitutes(params) {
  try {
    yield put(startPostSubstitutes())
    yield put(postSubstitutesStart())
    yield call(post, 'approval_substitutes', params, url)
    yield put(postSubstitutesSuccess())
  } catch(error) {
    yield put(patchSubstitutesError(error))
  }
}

function* deleteSubstitutes(id) {
  try {
    yield put(startDeleteSubstitutes())
    yield put(deleteSubstitutesStart())
    yield call(del, `${url}/${id}`)
    yield put(deleteSubstitutesSuccess())
  } catch(error) {
    yield put(deleteSubstitutesError(error))
  }
}

const renameKeyToUsername = R.compose(
  R.fromPairs,
  R.map(([k, v]) => ([v.username, v ])),
  R.toPairs,
  R.path(['entities', 'approval_substitute_views'])
)

function* fetchSubstitutesHandler(action) {
  const data = yield call(fetchSubstitutes)
  const substitutes = renameKeyToUsername(data)
  yield put(updateSubstitutes(substitutes))
  // initially get all the photos as well
  if (data) {
    const usernames = yield select(employeeUsernameSelector)
    const missingUserImages = R.difference(Object.keys(substitutes), usernames)

    for (let user of missingUserImages) {
      yield put(fetchEmployeeImage(user))
    }
  }
}

function* postSubstitutesHandler(action) {
  const { substitute: { image, ...params } } = action
  yield call(postSubstitutes, params)
}

function* patchSubstitutesHandler(action) {
  const { substitute: { id, image, ...params } } = action
  yield call(patchSubstitutes, id, params)
}

function* deleteSubstitutesHandler(action) {
  const { substitute: { id, username } } = action
  if (id)
    yield call(deleteSubstitutes, id)
  yield put(removeSubstitutes(username))
}

function* saveSubstitutesHandler() {
  //select all values that dont' have id values
  const pendingQueries = []
  const createSubstitutes = yield select(createSubstituteSelector)
  const updateSubstitutes = yield select(patchSubstituteSelector)

  for( let substitute of createSubstitutes ) {
    const { image, ...params } = substitute
    pendingQueries.push(yield fork(postSubstitutes, params))
  }

  for( let substitute of updateSubstitutes ) {
    const { id, image, ...params } = substitute
    pendingQueries.push(yield fork(patchSubstitutes, id, params))
  }

  if (pendingQueries.length > 0)
    yield join(...pendingQueries)

  yield put(fetchSubstitutesAction())
}

function* watchSaveSubstitutes() {
  yield takeEvery(SAVE_SUBSTITUTES, saveSubstitutesHandler)
}

function* watchFetchSubstitutes() {
  yield takeEvery(FETCH_SUBSTITUTES, fetchSubstitutesHandler)
}

function* watchPatchSubstitutes() {
  yield takeEvery(PATCH_SUBSTITUTES, patchSubstitutesHandler)
}

function* watchDeleteSubstitutes() {
  yield takeEvery(DELETE_SUBSTITUTES, deleteSubstitutesHandler)
}

function* watchPostSubstitutes() {
  yield takeEvery(POST_SUBSTITUTES, postSubstitutesHandler)
}

function* loadSagas() {
  yield all([
    fork(watchFetchSubstitutes),
    fork(watchPostSubstitutes),
    fork(watchPatchSubstitutes),
    fork(watchSaveSubstitutes),
    fork(watchDeleteSubstitutes),
  ])
}

export default loadSagas
